import { useDashboardFilters } from "@/contexts/DashboardStateContext";
import { useDashboardLocation } from "@/hooks/useDashboardLocation";
import { api } from "@/lib/api/api";
import {
  IWorkingLocationPresenceType,
  WL_PRESENCE_OFFICE
} from "@gettactic/api";
import { AgendaDay as AgendaDayType } from "@gettactic/api";
import { getObjectKeys } from "@gettactic/helpers";
import { Button } from "@v2/components/ui/button";
import { TooltipProvider } from "@v2/components/ui/tooltip";
import { AuthenticatedUserRow } from "@webV2/components/TeamSchedule/AuthenticatedUserRow";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React from "react";
import { AgendaDay } from "./AgendaDay";

const weekDays = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday"
];

export interface AgendaWeekProps {
  locationData: ReturnType<typeof useDashboardLocation>;
  showAsMobileDaily?: boolean;
  selectedDayIndex?: number;
  setSelectedDayIndex?: (index: number) => void;
  daysCount?: number;
  onPrevWeek?: () => void;
  onNextWeek?: () => void;
}

const AgendaWeekContent: React.FC<AgendaWeekProps> = ({
  locationData,
  showAsMobileDaily,
  selectedDayIndex,
  setSelectedDayIndex,
  daysCount,
  onPrevWeek,
  onNextWeek
}) => {
  const {
    scheduleMeData,
    officeAvailabilityData,
    isLoadingOfficeAvailability,
    updateWorkingLocation,
    getLocationForDate,
    isLocationLoading,
    weekDate,
    authenticatedUser
  } = locationData;

  const days = getObjectKeys(scheduleMeData.data?.days || {});

  const { selectedEvent, setSelectedEvent } = useDashboardFilters();
  const mobileDailyRef = React.useRef<HTMLDivElement>(null);

  const hasAnyAgendaLocationAvailable =
    scheduleMeData.data?.agenda &&
    Object.keys(scheduleMeData.data?.agenda).some(
      (date) => scheduleMeData.data?.agenda[date]?.location !== "not_available"
    );

  return (
    <>
      {showAsMobileDaily && (
        <div
          className={
            "w-full max-w-full overflow-auto rounded-lg border-1 border-neutral-200 bg-white shadow-sm"
          }
        >
          <div className="flex items-center justify-between px-2 pt-2">
            <Button variant="ghost" size="icon" onClick={onPrevWeek}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h2 className="font-medium text-sm">
              {format(new Date(weekDate), "MMMM yyyy")}
            </h2>
            <Button variant="ghost" size="icon" onClick={onNextWeek}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className={"flex w-full gap-0 px-0"} ref={mobileDailyRef}>
            <TooltipProvider>
              <AuthenticatedUserRow
                authenticatedUser={authenticatedUser}
                headers={[]}
                favsApi={api}
                columnCount={daysCount || 0}
                selectedDayIndex={selectedDayIndex}
                setSelectedDayIndex={setSelectedDayIndex}
                showMobileCells
              />
            </TooltipProvider>
          </div>
        </div>
      )}
      <div className="md:-mt-[7px] flex flex-col gap-6">
        {days?.slice(0, (weekDays || []).length).map((date, index) => {
          if (
            hasAnyAgendaLocationAvailable &&
            scheduleMeData?.data?.agenda?.[date]?.location === "not_available"
          )
            return null;
          if (showAsMobileDaily && index !== selectedDayIndex) return;
          // We have to cast here because there is an agenda for each day
          const todaysAgenda = scheduleMeData.data?.agenda[
            date
          ] as AgendaDayType;
          const todaysOfficeAvailability = officeAvailabilityData?.days?.[date]?.[todaysAgenda?.office_id ?? ''] ?? null;
          const location = todaysAgenda?.location;
          const officeId = todaysAgenda?.office_id;

          // Get the effective location data (actual or optimistic)
          const effectiveDay = getLocationForDate(
            date,
            scheduleMeData.data?.agenda[date] || null
          );

          const isLoading = isLocationLoading(date);

          return (
            <AgendaDay
              key={`dash_schedule_${date}_${location}`}
              office_id={officeId}
              date={date}
              location={
                location !== WL_PRESENCE_OFFICE
                  ? (location as IWorkingLocationPresenceType)
                  : null
              }
              workingLocationId={officeId ?? undefined}
              schedule={scheduleMeData.data?.agenda[date]?.schedules ?? []}
              onSelectItemChange={setSelectedEvent}
              selectedEvent={selectedEvent}
              day={todaysAgenda || null}
              isLoadingOfficeAvailability={isLoadingOfficeAvailability}
              officeAvailability={todaysOfficeAvailability}
              onLocationChange={(newLocation) => {
                updateWorkingLocation(newLocation, date, index, todaysAgenda);
              }}
              isLocationLoading={isLoading}
              optimisticLocation={
                effectiveDay &&
                todaysAgenda &&
                effectiveDay.location !== todaysAgenda.location
                  ? effectiveDay
                  : effectiveDay?.office_id !== todaysAgenda.office_id
                    ? effectiveDay
                    : null
              }
              isMobile={showAsMobileDaily}
              orgSlug={authenticatedUser.slug ?? undefined}
            />
          );
        })}
      </div>
    </>
  );
};

export const AgendaWeek: React.FC<AgendaWeekProps> = (props) => {
  return <AgendaWeekContent {...props} />;
};
